import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Loader2, ImagePlus, X, Pin, Trash2,
  Eye, EyeOff, Tag, Plus, FolderOpen,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import api, { mediaUrl } from '../api';
import MediaPicker from '../components/MediaPicker';
import { toast } from 'sonner';

const blank = {
  title: '',
  excerpt: '',
  body: '',
  featured_image: '',
  author_name: '',
  tags: [],
  is_pinned: false,
  status: 'published',
};

export default function NewsEditor() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await api.get(`/news/${id}`);
        setForm({
          title: data.title || '',
          excerpt: data.excerpt || '',
          body: data.body || '',
          featured_image: data.featured_image || '',
          author_name: data.author_name || '',
          tags: data.tags || [],
          is_pinned: !!data.is_pinned,
          status: data.status || 'published',
        });
      } catch (e) {
        toast.error('Failed to load post');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/media/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      update('featured_image', data.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (form.tags.includes(t)) {
      setTagInput('');
      return;
    }
    update('tags', [...form.tags, t]);
    setTagInput('');
  };

  const removeTag = (t) => update('tags', form.tags.filter((x) => x !== t));

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required.');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post('/news', form);
        toast.success('Post created');
        navigate(`/admin/news/${data.id}`, { replace: true });
      } else {
        await api.put(`/news/${id}`, form);
        toast.success('Saved');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete “${form.title}”? This cannot be undone.`)) return;
    try {
      await api.delete(`/news/${id}`);
      toast.success('Post deleted');
      navigate('/admin/news');
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-white/50">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  const imgUrl = form.featured_image ? mediaUrl(form.featured_image) : '';

  return (
    <div className="max-w-6xl">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-7">
        <div className="flex items-center gap-4 min-w-0">
          <Link to="/admin/news" className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#5DCAB5] font-semibold">{isNew ? 'New Post' : 'Edit Post'}</div>
            <div className="font-serif-display text-[24px] font-bold truncate">{form.title || 'Untitled post'}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <Button onClick={handleDelete} variant="ghost" className="h-10 rounded-full text-white/55 hover:text-red-400 hover:bg-red-500/10 font-semibold">
              <Trash2 className="h-4 w-4 mr-1.5" /> Delete
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving} className="h-10 rounded-full bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold px-5 btn-press shadow-[0_8px_20px_-6px_rgba(229,97,76,0.5)]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> {isNew ? 'Publish Post' : 'Save Changes'}</>}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <Card>
            <Field label="Post Title *">
              <Input value={form.title} onChange={(e) => update('title', e.target.value)} placeholder="e.g. New Summer Programs Open for Enrollment" className={inputCls} />
            </Field>
            <Field label="Excerpt / Summary">
              <Textarea rows={2} value={form.excerpt} onChange={(e) => update('excerpt', e.target.value)} placeholder="A short summary shown on cards and listings." className={textareaCls} />
            </Field>
            <Field label="Body">
              <Textarea rows={14} value={form.body} onChange={(e) => update('body', e.target.value)} placeholder="Write your post here. Line breaks become paragraphs." className={`${textareaCls} font-serif-display text-[15px]`} />
            </Field>
          </Card>
        </div>

        <div className="space-y-5">
          <Card title="Publish" icon={Eye}>
            <Field label="Status">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button" onClick={() => update('status', 'published')}
                  className={`h-10 rounded-lg border text-[13px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${
                    form.status === 'published'
                      ? 'bg-[#5DCAB5]/15 border-[#5DCAB5]/40 text-[#5DCAB5]'
                      : 'bg-[#0f0d12] border-white/10 text-white/55 hover:text-white'
                  }`}
                ><Eye className="h-3.5 w-3.5" /> Published</button>
                <button
                  type="button" onClick={() => update('status', 'draft')}
                  className={`h-10 rounded-lg border text-[13px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${
                    form.status === 'draft'
                      ? 'bg-white/10 border-white/20 text-white'
                      : 'bg-[#0f0d12] border-white/10 text-white/55 hover:text-white'
                  }`}
                ><EyeOff className="h-3.5 w-3.5" /> Draft</button>
              </div>
            </Field>
            <div className="flex items-center justify-between py-2">
              <div>
                <div className="text-[13px] font-semibold text-white inline-flex items-center gap-1.5">
                  <Pin className="h-3.5 w-3.5 text-[#FCD968]" fill={form.is_pinned ? '#FCD968' : 'transparent'} /> Pinned post
                </div>
                <div className="text-[11px] text-white/45 mt-0.5">Stick to the top of the list</div>
              </div>
              <Switch checked={form.is_pinned} onCheckedChange={(v) => update('is_pinned', v)} />
            </div>
            <Field label="Author Name">
              <Input value={form.author_name} onChange={(e) => update('author_name', e.target.value)} placeholder="e.g. GEMS @ The Arts Team" className={inputCls} />
            </Field>
          </Card>

          <Card title="Tags" icon={Tag}>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="Add a tag"
                className={inputCls}
              />
              <Button type="button" onClick={addTag} className="h-11 rounded-lg bg-white/5 hover:bg-white/10 text-white border border-white/10 px-3">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {form.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {form.tags.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E5614C]/15 text-[#E5614C] text-[12px] font-semibold">
                    {t}
                    <button onClick={() => removeTag(t)} className="hover:text-white">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </Card>

          <Card title="Featured Image" icon={ImagePlus}>
            {imgUrl ? (
              <div className="relative rounded-xl overflow-hidden bg-[#0f0d12] border border-white/[0.06] aspect-[16/10] group">
                <img src={imgUrl} alt="featured" className="w-full h-full object-cover" />
                <button onClick={() => update('featured_image', '')} className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/60 backdrop-blur-sm text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" title="Remove">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="aspect-[16/10] rounded-xl border-2 border-dashed border-white/10 flex flex-col items-center justify-center text-white/40 text-[12px]">
                <ImagePlus className="h-6 w-6 mb-1" />
                No image yet
              </div>
            )}
            <Button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="mt-3 w-full h-10 rounded-lg bg-white/5 hover:bg-white/10 text-white font-semibold border border-white/10">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ImagePlus className="h-4 w-4 mr-1.5" /> Upload Image</>}
            </Button>
            <Button type="button" onClick={() => setPickerOpen(true)}
              className="mt-2 w-full h-10 rounded-lg bg-white/5 hover:bg-white/10 text-white font-semibold border border-white/10">
              <FolderOpen className="h-4 w-4 mr-1.5" /> Choose from Library
            </Button>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
            <Input
              value={form.featured_image || ''} onChange={(e) => update('featured_image', e.target.value)}
              placeholder="Or paste an image URL"
              className={`${inputCls} mt-2 text-[12px]`}
            />
            <MediaPicker
              open={pickerOpen}
              onClose={() => setPickerOpen(false)}
              onSelect={(item) => update('featured_image', item.url)}
              title="Pick a featured image"
            />
          </Card>
        </div>
      </div>
    </div>
  );
}

const inputCls = 'h-11 rounded-lg bg-[#0f0d12] border-white/10 text-white text-[14px] placeholder:text-white/30 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]';
const textareaCls = 'rounded-lg bg-[#0f0d12] border-white/10 text-white text-[14px] placeholder:text-white/30 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C] resize-none leading-relaxed';

function Card({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl bg-[#16131a] border border-white/[0.06] p-5 space-y-4">
      {title && (
        <div className="flex items-center gap-2 text-white/80">
          {Icon && <Icon className="h-4 w-4 text-[#E5614C]" />}
          <div className="font-semibold text-[13px] uppercase tracking-[0.14em]">{title}</div>
        </div>
      )}
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-white/75 text-[12px] font-semibold uppercase tracking-[0.12em]">{label}</Label>
      {children}
    </div>
  );
}
